import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  UserPlus, Trash2, KeyRound, ShieldOff, ShieldCheck, Loader2, X, User,
  CheckCircle2, XCircle,
} from "lucide-react";

/**
 * Multi-admin management. Lets any existing admin:
 *   • see who else has admin access
 *   • create a new admin (email + temp password)
 *   • activate / deactivate an admin
 *   • reset another admin's password
 *   • delete an admin (except themselves, or the last remaining one)
 *
 * Change-your-own-password uses a separate, safer flow (requires the current
 * password) — see the ChangePasswordCard below.
 */
export default function TeamAdmin() {
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [resetTarget, setResetTarget] = useState(null); // email being reset

  const load = async () => {
    setLoading(true);
    try {
      const [meR, uR] = await Promise.all([
        api.get("/auth/me"),
        api.get("/admin/users"),
      ]);
      setMe(meR.data);
      setUsers(uR.data);
    } catch {
      toast.error("Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    const to = !u.is_active;
    if (u.is_self) return;
    try {
      await api.put(`/admin/users/${encodeURIComponent(u.email)}`, { is_active: to });
      toast.success(to ? "Admin reactivated" : "Admin deactivated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (u) => {
    if (u.is_self) return;
    if (!window.confirm(`Delete admin ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${encodeURIComponent(u.email)}`);
      toast.success("Admin removed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="flex items-center justify-center py-32 text-ink/50"><Loader2 className="animate-spin" /><span className="ml-2">Loading…</span></div>;

  return (
    <div className="space-y-8" data-testid="team-admin">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-2xl md:text-3xl text-ink">Team</h1>
          <p className="text-sm text-ink/60 mt-1 max-w-2xl">
            Every person in this list can log into the admin panel. Add trusted volunteers or board members
            when they need to help manage the site — and deactivate or remove them when they no longer do.
          </p>
        </div>
        <button
          onClick={() => setCreating(true)}
          data-testid="team-add-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-navy text-cream text-sm font-medium hover:bg-navy/90 shadow-sm"
        >
          <UserPlus size={16} /> Add Admin
        </button>
      </div>

      {/* Change my own password */}
      <ChangePasswordCard />

      {/* Team list */}
      <div className="rounded-2xl bg-white border border-sand overflow-hidden">
        <div className="px-5 py-3 border-b border-sand bg-cream/40">
          <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Administrators</div>
          <div className="text-xs text-ink/55 mt-0.5">{users.length} account{users.length === 1 ? "" : "s"}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream/60 text-ink/55">
              <tr>
                <th className="text-left px-5 py-3 font-medium text-[11px] uppercase tracking-wider">Admin</th>
                <th className="text-left px-5 py-3 font-medium text-[11px] uppercase tracking-wider">Status</th>
                <th className="text-left px-5 py-3 font-medium text-[11px] uppercase tracking-wider">Last Login</th>
                <th className="text-right px-5 py-3 font-medium text-[11px] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.email} className="border-t border-sand/60" data-testid={`team-row-${u.email}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gold/20 flex items-center justify-center text-navy shrink-0">
                        <User size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-ink truncate">{u.name || u.email.split("@")[0]}</div>
                        <div className="text-xs text-ink/55 truncate">{u.email}</div>
                      </div>
                      {u.is_self && (
                        <span className="text-[10px] uppercase tracking-wider bg-gold/20 text-navy px-2 py-0.5 rounded-full">
                          You
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {u.is_active ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                        <CheckCircle2 size={13} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-ink/40">
                        <XCircle size={13} /> Deactivated
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-xs text-ink/55">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {!u.is_self && (
                        <>
                          <button
                            onClick={() => setResetTarget(u.email)}
                            title="Reset password"
                            data-testid={`team-reset-${u.email}`}
                            className="p-2 rounded hover:bg-sand/60 text-ink/60"
                          >
                            <KeyRound size={15} />
                          </button>
                          <button
                            onClick={() => toggleActive(u)}
                            title={u.is_active ? "Deactivate" : "Reactivate"}
                            data-testid={`team-toggle-${u.email}`}
                            className="p-2 rounded hover:bg-sand/60 text-ink/60"
                          >
                            {u.is_active ? <ShieldOff size={15} /> : <ShieldCheck size={15} />}
                          </button>
                          <button
                            onClick={() => remove(u)}
                            title="Delete"
                            data-testid={`team-delete-${u.email}`}
                            className="p-2 rounded hover:bg-red-50 text-red-600"
                          >
                            <Trash2 size={15} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && <AddAdminModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {resetTarget && (
        <ResetPasswordModal
          email={resetTarget}
          onClose={() => setResetTarget(null)}
          onSaved={() => { setResetTarget(null); load(); }}
        />
      )}
    </div>
  );
}

/* ============================ Change-own-password ============================ */
function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (form.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl bg-navy text-cream p-6 md:p-7 relative overflow-hidden">
      <div className="absolute inset-0 pattern-girih opacity-15 pointer-events-none" />
      <form onSubmit={submit} className="relative">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
            <KeyRound size={18} className="text-gold" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Your Account</div>
            <h2 className="font-serif text-xl">Change password</h2>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <input
            type="password"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            placeholder="Current password"
            autoComplete="current-password"
            data-testid="cp-current"
            className="px-4 py-3 rounded-lg bg-navy-800/50 border border-cream/15 focus:border-gold focus:outline-none placeholder-cream/40 text-cream"
          />
          <input
            type="password"
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            placeholder="New password (min 8 chars)"
            autoComplete="new-password"
            data-testid="cp-new"
            className="px-4 py-3 rounded-lg bg-navy-800/50 border border-cream/15 focus:border-gold focus:outline-none placeholder-cream/40 text-cream"
          />
          <input
            type="password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            placeholder="Confirm new password"
            autoComplete="new-password"
            data-testid="cp-confirm"
            className="px-4 py-3 rounded-lg bg-navy-800/50 border border-cream/15 focus:border-gold focus:outline-none placeholder-cream/40 text-cream"
          />
        </div>
        <div className="text-[11px] text-cream/55 mt-2">
          Must include at least one letter and one number or symbol.
        </div>
        <button
          type="submit"
          disabled={saving || !form.current_password || !form.new_password}
          data-testid="cp-submit"
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold text-navy font-semibold text-sm hover:bg-gold/90 disabled:opacity-50"
        >
          {saving && <Loader2 size={14} className="animate-spin" />}
          Update Password
        </button>
      </form>
    </div>
  );
}

/* ================================ Add admin ================================= */
function AddAdminModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.password) {
      toast.error("Email and password required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/users", form);
      toast.success(`Admin created — share ${form.email} the temp password securely`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Add administrator" testid="team-add-modal">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Email">
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="name@example.com"
            data-testid="team-add-email"
            className="w-full px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy"
            autoFocus
            required
          />
        </Field>
        <Field label="Name" hint="Optional — shows in the team list.">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Br. Yusuf"
            data-testid="team-add-name"
            className="w-full px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy"
          />
        </Field>
        <Field label="Temporary password" hint="Share it with them securely. They can change it themselves after logging in.">
          <input
            type="text"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Min 8 chars, letters + digits/symbols"
            data-testid="team-add-password"
            className="w-full px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy font-mono"
          />
        </Field>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-ink/70 hover:bg-sand/60">Cancel</button>
          <button type="submit" disabled={saving} data-testid="team-add-submit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy text-cream text-sm font-medium hover:bg-navy/90 disabled:opacity-60">
            {saving && <Loader2 size={14} className="animate-spin" />}
            Create Admin
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ============================ Reset password ================================ */
function ResetPasswordModal({ email, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/users/${encodeURIComponent(email)}`, { new_password: pw });
      toast.success(`Password reset — share the new password with ${email} securely`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal onClose={onClose} title={`Reset password — ${email}`} testid="team-reset-modal">
      <form onSubmit={submit} className="space-y-4">
        <div className="text-sm text-ink/65 bg-cream/50 rounded-lg p-3 leading-relaxed">
          You&apos;re resetting <strong>{email}</strong>&apos;s password. Copy the new password from this screen —
          you won&apos;t be able to see it again.
        </div>
        <Field label="New password">
          <input
            type="text"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Min 8 chars, letters + digits/symbols"
            data-testid="team-reset-pw"
            className="w-full px-3 py-2 rounded-lg border border-sand bg-white focus:outline-none focus:border-navy font-mono"
            autoFocus
          />
        </Field>
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-ink/70 hover:bg-sand/60">Cancel</button>
          <button type="submit" disabled={saving} data-testid="team-reset-submit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy text-cream text-sm font-medium hover:bg-navy/90 disabled:opacity-60">
            {saving && <Loader2 size={14} className="animate-spin" />}
            Reset Password
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ================================ Primitives ================================ */
function Modal({ onClose, title, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid={testid}>
      <div className="bg-white rounded-2xl w-full max-w-md mt-12 mb-12 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-sand">
          <h3 className="font-serif text-xl text-ink">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-sand/60 text-ink/60"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-ink/55 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-ink/45 mt-1">{hint}</p>}
    </div>
  );
}
